import { clamp01, expSmooth } from '../core/math';
import { LM, SKELETON } from '../pose/landmarks';
import type { PoseFrame } from '../pose/types';
import type { Feedback, Match } from '../sim/match';
import { drawOpponent, PUNCH_COLOUR } from './opponent';

interface Floater {
  text: string;
  detail: string;
  born: number;
  who: 'player' | 'ai';
  good: boolean;
  x: number;
}

/**
 * Canvas 2-D renderer.
 *
 * Rejected alternatives:
 *  - three.js / WebGL: real cost is not the draw calls, it is that BlazePose is
 *    already holding the GPU. On integrated graphics the pose model and a 3-D
 *    scene contend for the same execution units and the *pose* framerate drops,
 *    which degrades the thing the game actually depends on. Trading input
 *    fidelity for visual fidelity is the wrong trade in a reaction trainer.
 *  - DOM/CSS: fine for HUD, hopeless for 60 fps sprite motion.
 *  - PixiJS: a reasonable upgrade path if particle counts grow, but it is 400 kB
 *    to draw ~20 primitives per frame. Not yet justified.
 *
 * The renderer is stateless with respect to gameplay: it reads a snapshot and
 * draws. That keeps replay rendering (from a recorded event log, with no camera)
 * essentially free.
 */
export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private floaters: Floater[] = [];
  private hitFlashAi = 0;
  private hitFlashPlayer = 0;
  private dpr = 1;
  /** Smoothed values so the HUD does not strobe on noisy per-frame inputs. */
  private smooth = { gap: 1.32, playerPoise: 100, aiPoise: 100, quality: 1 };

  showSkeleton = true;
  showDebug = false;

  constructor(
    private canvas: HTMLCanvasElement,
    private video: HTMLVideoElement,
  ) {
    const c = canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!c) throw new Error('2D canvas unavailable');
    this.ctx = c;
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  resize(): void {
    // Cap DPR at 2: beyond that we are shading 4x the pixels for a difference no
    // one can see while a boxing glove is flying at them.
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
  }

  pushFeedback(f: Feedback): void {
    const good = f.who === 'player' ? f.outcome === 'clean' || f.outcome === 'glancing' : f.outcome === 'slipped' || f.outcome === 'miss' || f.outcome === 'blocked';
    this.floaters.push({
      text: f.label,
      detail: f.detail,
      born: performance.now(),
      who: f.who,
      good,
      x: (Math.random() - 0.5) * 0.2,
    });
    if (this.floaters.length > 8) this.floaters.shift();
    if (f.who === 'player' && (f.outcome === 'clean' || f.outcome === 'glancing')) this.hitFlashAi = 1;
    if (f.who === 'ai' && (f.outcome === 'clean' || f.outcome === 'glancing')) this.hitFlashPlayer = 1;
  }

  render(match: Match, frame: PoseFrame | null, now: number, dtMs: number): void {
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;

    this.hitFlashAi = expSmooth(this.hitFlashAi, 0, dtMs, 90);
    this.hitFlashPlayer = expSmooth(this.hitFlashPlayer, 0, dtMs, 110);
    this.smooth.gap = expSmooth(this.smooth.gap, match.gap, dtMs, 70);
    this.smooth.playerPoise = expSmooth(this.smooth.playerPoise, match.player.vitals.poise, dtMs, 180);
    this.smooth.aiPoise = expSmooth(this.smooth.aiPoise, match.ai.vitals.poise, dtMs, 180);
    this.smooth.quality = expSmooth(this.smooth.quality, frame?.quality ?? 0, dtMs, 250);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#0b0d12';
    ctx.fillRect(0, 0, w, h);

    this.drawBackground(w, h);

    drawOpponent(ctx, w, h, {
      ai: match.aiSnapshot(),
      gap: this.smooth.gap,
      lateral: match.lateral,
      telegraph: null,
      now,
      hitFlash: this.hitFlashAi,
    });

    if (this.showSkeleton && frame) this.drawSkeleton(frame, w, h);

    this.drawReactionBars(w, h, match);
    this.drawFloaters(w, h, now);

    if (this.hitFlashPlayer > 0.01) {
      ctx.fillStyle = `rgba(200,40,40,${this.hitFlashPlayer * 0.32})`;
      ctx.fillRect(0, 0, w, h);
    }
    if (this.smooth.quality < 0.6) this.drawTrackingWarning(w, h);
  }

  private drawBackground(w: number, h: number): void {
    const ctx = this.ctx;
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#141a24');
    g.addColorStop(0.62, '#0e131b');
    g.addColorStop(1, '#070a0f');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // Canvas floor + ropes, drawn in perspective. Sells depth for almost nothing.
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 2 * this.dpr;
    for (let i = 1; i <= 5; i++) {
      const y = h * (0.68 + i * 0.065);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(240,220,180,0.09)';
    ctx.lineWidth = 6 * this.dpr;
    for (const ry of [0.24, 0.34, 0.44]) {
      ctx.beginPath();
      ctx.moveTo(0, h * ry);
      ctx.lineTo(w, h * ry);
      ctx.stroke();
    }
  }

  /**
   * The overlay is aligned to the real, CSS-positioned <video> element rather
   * than to a hard-coded rectangle, so it keeps registration whether the preview
   * is the small corner self-view or the large centred calibration view.
   *
   * Note the video is mirrored in CSS (`scaleX(-1)`), so image-space x maps to
   * screen as `1 - x`.
   */
  private drawSkeleton(f: PoseFrame, _w: number, _h: number): void {
    const ctx = this.ctx;
    if (this.video.readyState < 2) return;
    const r = this.video.getBoundingClientRect();
    if (r.width < 8 || r.height < 8) return;
    const d = this.dpr;
    const ox = r.left * d;
    const oy = r.top * d;
    const vw = r.width * d;
    const vh = r.height * d;
    const px = (i: number) => ox + (1 - f.image[i].x) * vw;
    const py = (i: number) => oy + f.image[i].y * vh;

    ctx.lineWidth = 2 * this.dpr;
    ctx.strokeStyle = 'rgba(90,220,255,0.8)';
    ctx.beginPath();
    for (const [a, b] of SKELETON) {
      if (f.vis[a] < 0.4 || f.vis[b] < 0.4) continue;
      ctx.moveTo(px(a), py(a));
      ctx.lineTo(px(b), py(b));
    }
    ctx.stroke();

    // Gloves highlighted — the two landmarks the whole game hangs on.
    for (const i of [LM.LEFT_WRIST, LM.RIGHT_WRIST]) {
      ctx.beginPath();
      ctx.arc(px(i), py(i), 4 * this.dpr, 0, Math.PI * 2);
      ctx.fillStyle = '#ff5a4a';
      ctx.fill();
    }
  }

  /**
   * The two bars that replace the range bar in punch-out.
   *
   * THREAT fills during the opponent's wind-up. It is the reaction stimulus made
   * explicit — the player learns to associate the animation's tell with the bar,
   * and then stops needing the bar. On the lowest tiers it also names the dodge.
   *
   * WINDOW counts down the punish opportunity. Both are placed low and central,
   * under the opponent, so they sit in peripheral vision while the player is
   * looking at the gloves — which is where they should be looking.
   */
  private drawReactionBars(w: number, h: number, match: Match): void {
    const ctx = this.ctx;
    const d = this.dpr;
    const bw = w * 0.34;
    const x = w * 0.5 - bw / 2;
    const y = h - 42 * d;

    const snap = match.aiSnapshot();
    const tel = snap.telegraphing;
    const open = match.exchange.openness;

    if (open.remainingMs > 0) {
      // Punish window. Deliberately loud: this is the moment the player is being
      // trained to notice and act on.
      const total = Math.max(1, match.exchange.tuning.windowMs * 2);
      const frac = clamp01(open.remainingMs / total);
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      ctx.fillRect(x, y, bw, 12 * d);
      const grad = ctx.createLinearGradient(x, 0, x + bw, 0);
      grad.addColorStop(0, '#5ce08a');
      grad.addColorStop(1, '#d8f36a');
      ctx.fillStyle = grad;
      ctx.fillRect(x, y, bw * frac, 12 * d);

      const pulse = 0.75 + 0.25 * Math.sin(performance.now() / 90);
      ctx.globalAlpha = pulse;
      ctx.fillStyle = '#5ce08a';
      ctx.font = `800 ${17 * d}px ui-sans-serif, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(
        open.reason === 'perfect-dodge' ? 'PERFECT — HIT HIM' : 'OPEN — HIT HIM',
        w * 0.5,
        y - 10 * d,
      );
      ctx.globalAlpha = 1;
      if (match.exchange.hitsThisWindow > 0) {
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.font = `700 ${13 * d}px ui-sans-serif, system-ui, sans-serif`;
        ctx.fillText(`${match.exchange.hitsThisWindow} landed`, w * 0.5, y + 26 * d);
      }
      return;
    }

    if (tel) {
      // The bar carries the punch's own colour, so the HUD cue and the animation
      // cue teach the same association instead of competing. Once learned, the
      // player stops needing the bar at all — which is the goal.
      const c = PUNCH_COLOUR[tel.type];
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      ctx.fillRect(x, y, bw, 12 * d);
      ctx.fillStyle = tel.progress > 0.85 ? '#ff4b3a' : c;
      ctx.fillRect(x, y, bw * clamp01(tel.progress), 12 * d);

      // A tick at the end of the hold phase: past this point the punch is
      // already on its way and reacting is too late.
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.fillRect(x + bw * 0.62 - 1 * d, y - 3 * d, 2 * d, 18 * d);

      ctx.textAlign = 'center';
      ctx.fillStyle = c;
      ctx.font = `800 ${17 * d}px ui-sans-serif, system-ui, sans-serif`;
      ctx.fillText(this.dodgePrompt(match, tel), w * 0.5, y - 10 * d);

      // Naming the punch turns "something is coming" into a learnable
      // vocabulary — you can attach a word to the silhouette you just saw. But
      // it also hands over the read, so it is gated to the same bottom two tiers
      // as the dodge prompt. Above Easy the colour and the pose are the answer.
      if (match.showDodgePrompt) {
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.font = `700 ${11 * d}px ui-sans-serif, system-ui, sans-serif`;
        ctx.fillText(tel.type.toUpperCase(), w * 0.5, y + 26 * d);
      }
      return;
    }

    // Idle: a thin neutral rail so the bar's position is learned before it matters.
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.fillRect(x, y + 4 * d, bw, 4 * d);
  }

  /**
   * On the bottom two tiers we name the correct response. Above that we only say
   * that something is coming — working out *what* is the entire skill, and
   * prompting for it would hollow the game out.
   */
  private dodgePrompt(match: Match, tel: NonNullable<ReturnType<Match['aiSnapshot']>['telegraphing']>): string {
    if (!match.showDodgePrompt) return 'INCOMING';
    if (tel.target === 'body') return 'BLOCK BODY';
    switch (tel.type) {
      case 'uppercut':
        return 'LEAN BACK';
      case 'hook':
        return tel.side === 'left' ? 'DUCK / SLIP RIGHT' : 'DUCK / SLIP LEFT';
      default:
        return tel.side === 'left' ? 'SLIP RIGHT' : 'SLIP LEFT';
    }
  }

  private drawFloaters(w: number, h: number, now: number): void {
    const ctx = this.ctx;
    ctx.textAlign = 'center';
    for (let i = this.floaters.length - 1; i >= 0; i--) {
      const f = this.floaters[i];
      const age = (now - f.born) / 1100;
      if (age >= 1) {
        this.floaters.splice(i, 1);
        continue;
      }
      const a = 1 - age * age;
      const y = h * (f.who === 'player' ? 0.34 : 0.5) - age * 46 * this.dpr;
      const x = w * (0.5 + f.x);
      ctx.globalAlpha = a;
      ctx.fillStyle = f.good ? '#5ce08a' : '#ff6b5a';
      ctx.font = `800 ${26 * this.dpr}px ui-sans-serif, system-ui, sans-serif`;
      ctx.fillText(f.text, x, y);
      if (f.detail) {
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.font = `${13 * this.dpr}px ui-sans-serif, system-ui, sans-serif`;
        ctx.fillText(f.detail, x, y + 18 * this.dpr);
      }
      ctx.globalAlpha = 1;
    }
  }

  private drawTrackingWarning(w: number, h: number): void {
    const ctx = this.ctx;
    ctx.fillStyle = 'rgba(255,180,60,0.9)';
    ctx.font = `700 ${14 * this.dpr}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText('Tracking is poor — step back so your hips and head are both in frame', w * 0.5, h * 0.08);
  }
}
